/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import Avatar from '@mui/material/Avatar';
import MenuItem from '@mui/material/MenuItem';
import { ButtonBase, Divider, Stack, useTheme } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
// Icons
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { authState } from '../../store/auth';
import { userState } from '../../store/user';
import { useTranslation } from 'react-i18next';
import { CompanyEmployeesRoles } from '../../services/roles';
import { useLocation } from 'react-router';
import logoMark from '../../assets/logo-mark-dark.png';
import { Notifications } from './Notifications';
import { useLogoutMutation } from '../../queries/auth/logout';
import { useEffect } from 'react';
import { LoadingState } from '../../store/global-loading';
import { livePreviewState } from '../../store/live-preview';
import { TOP_BAR_HEIGHT } from 'constants/layout';
import { getActiveModule } from 'constants/modules';
import AllAppsButton from './AllAppsButton';
import { usePlatformHandoff } from '../../services/platform-handoff';

const settings = ['Logout'];

const TopBar: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const location = useLocation();

  const setAuth = useSetRecoilState(authState);
  const [user, setUser] = useRecoilState(userState);
  const setLivePreview = useSetRecoilState(livePreviewState);
  const isLivePreview = useRecoilValue(livePreviewState);
  const isPreview = isLivePreview === 'true';
  const activeModule = getActiveModule(location.pathname);
  const goToPlatform = usePlatformHandoff();

  const setIsLoading = useSetRecoilState(LoadingState);

  const { mutate: logout, status, isLoading } = useLogoutMutation();

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null,
  );

  useEffect(() => {
    if (status === 'success' || status === 'error') {
      onLogout();
    }
  }, [status]);

  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading]);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const onLogout = () => {
    setAuth({
      token: null,
      refreshToken: null,
    });
    setUser({});
    setLivePreview(null);
    sessionStorage.clear();
    localStorage.clear();
  };

  const onSettingOption = (option: string) => {
    handleCloseUserMenu();
    option === 'Logout' && logout();
  };

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`
    .trim()
    .toUpperCase();

  // Short rule between the two halves of each cluster, rather than a
  // full-height one.
  const clusterDivider = (
    <Divider
      orientation='vertical'
      sx={{ height: '24px', alignSelf: 'center' }}
    />
  );

  return (
    <AppBar
      position='sticky'
      color='transparent'
      sx={{
        zIndex: theme.zIndex.modal,
        bgcolor: 'background.paper',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: `${TOP_BAR_HEIGHT} !important`,
          paddingX: '38px',
          // Equal outer tracks, so the logo in the middle track is optically
          // centred no matter how wide the two clusters are.
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
        }}
      >
        <Stack direction='row' alignItems='center' spacing='16px'>
          <AllAppsButton />

          {activeModule && (
            <>
              <ChevronRight style={{color:'grey' , fontSize:'16px'}}></ChevronRight>
              <Typography
                sx={{
                  fontSize: '16px !important',
                  fontWeight: 600,
                  color: 'grey !important',
                }}
              >
                {activeModule.name}
              </Typography>
            </>
          )}
        </Stack>

        {/* The mark leads back to the platform launcher, same as "All apps" beside it —
            it is the other half of the same gesture, and both share one handoff. */}
        <ButtonBase
          onClick={goToPlatform}
          aria-label={
            isPreview ? 'All apps — live preview mode' : 'All apps'
          }
          title={isPreview ? 'Live preview mode' : 'All apps'}
          sx={{
            justifySelf: 'center',
            borderRadius: '8px',
            p: '4px',
            // An impersonated session gets a red ring. The border is always
            // present and only its colour changes, so the logo never shifts.
            border: '2px solid',
            borderColor: isPreview ? 'error.main' : 'transparent',
          }}
        >
          <Box component='img' src={logoMark} alt='' sx={{ height: '26px' }} />
        </ButtonBase>

        <Stack
          direction='row'
          alignItems='center'
          spacing='16px'
          sx={{ justifySelf: 'end' }}
        >
          <Notifications />

          {clusterDivider}

          <ButtonBase
            onClick={handleOpenUserMenu}
            sx={{
              gap: '10px',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '10px',
              pl: '6px',
              pr: '10px',
              py: '6px',
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'secondary.light',
                color: 'secondary.main',
                fontSize: '13px',
                fontWeight: 600,
              }}
            >
              {initials}
            </Avatar>

            <Box textAlign='left'>
              <Typography
                display='block'
                sx={{
                  fontSize: '14px !important',
                  fontWeight: 600,
                  lineHeight: 1.35,
                  color: 'primary.main',
                }}
              >
                {user.firstName} {user.lastName}
              </Typography>

              <Typography
                sx={{
                  fontSize: '13px !important',
                  lineHeight: 1.35,
                  color: 'text.secondary',
                }}
              >
                {t(`teamMembers.${user.role as CompanyEmployeesRoles}`)}
              </Typography>
            </Box>

            <KeyboardArrowDownIcon
              sx={{ color: 'text.secondary', fontSize: '20px' }}
            />
          </ButtonBase>

          <Menu
            sx={{ mt: '8px' }}
            id='menu-appbar'
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
          >
            {settings.map((setting) => (
              <MenuItem key={setting} onClick={() => onSettingOption(setting)}>
                <Typography textAlign='center'>{setting}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
